import React, { FC } from 'react'
import { BadgeCardContainer } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import type {
  CoreParkingArea,
  CoreParkingSpot,
} from '../../../common/validations/contractParkingSpotsSchema'
import { ParkingLegends } from './ParkingLegends'
import { StaticBadgeCard } from './ParkingBadgeCard'
import { areaAddressLabel, areaPriceLabel } from './uiSelectors'
import { QueueEntry } from '../../api/apiHooks/useAPIGetParkingQueue'
import { ParkingQueuePanel } from './ParkingQueuePanel'

export const ParkingArea: FC<{
  area: CoreParkingArea
  onOpenSpot: (spot: CoreParkingSpot) => void
  canReserveDisabledSpots: boolean
  reservationsEnabled: boolean
  queueItems?: QueueEntry[]
  userMetadata: {
    isImpersonator: boolean
  }
}> = ({
  area,
  onOpenSpot,
  canReserveDisabledSpots,
  reservationsEnabled,
  queueItems,
  userMetadata,
}) => {
  const { t, i18n } = useTranslation('parking')

  const { locationDetails } = area.location
  const addressLabel = areaAddressLabel(area)
  const priceLabel = areaPriceLabel(area, i18n.language, t, null)

  return (
    <section className="mb-5">
      <h3 className="text-base md:text-lg font-medium mb-3">
        {t('view.parkingArea')} {locationDetails}, {addressLabel}
      </h3>

      <ParkingQueuePanel
        parkingSpots={area.parkingSpots}
        queueItems={queueItems}
        costPoolId={area.costPoolId}
        userMetadata={userMetadata}
      />

      <BadgeCardContainer layout="grid">
        {(area.parkingSpots ?? []).map((s) => (
          <StaticBadgeCard
            key={s.rentableParkingSpotId}
            spot={s}
            canReserveDisabledSpots={canReserveDisabledSpots}
            reservationsEnabled={reservationsEnabled}
            onOpen={onOpenSpot}
            queueItems={queueItems}
          />
        ))}
      </BadgeCardContainer>

      {(area.parkingSpots?.length ?? 0) > 0 && <ParkingLegends />}

      {priceLabel && (
        <div className="mt-4 text-sm">
          <div className="font-medium">{t('price.label')}</div>
          <div>{priceLabel}</div>
        </div>
      )}
    </section>
  )
}
